import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Accordion, Toggle, InputField, IconProps } from '@ets/design-system';

import styles from './Section.module.scss';

type TextAlignmentProps = Pick<
  Editor.Edition.TableProperties,
  'textAlignment' | 'verticalAlignment'
> &
  SectionProps;

const DEFAULT_PROPERTIES: Pick<
  Editor.Edition.TableProperties,
  'textAlignment' | 'verticalAlignment'
> = {
  textAlignment: 'l',
  verticalAlignment: 'c',
};

type ToggleOption<
  T extends
    | Editor.Edition.TableProperties['textAlignment']
    | Editor.Edition.TableProperties['verticalAlignment'],
> = {
  value: T;
  icon: IconProps['icon'];
};

const HORIZONTAL_TOGGLES: ToggleOption<Editor.Edition.TableProperties['textAlignment']>[] = [
  {
    value: 'l',
    icon: 'align-left',
  },
  {
    value: 'c',
    icon: 'align-center',
  },
  {
    value: 'r',
    icon: 'align-right',
  },
  {
    value: 'j',
    icon: 'align-justify',
  },
];
const VERTICAL_TOGGLES: ToggleOption<Editor.Edition.TableProperties['verticalAlignment']>[] = [
  {
    value: 't',
    icon: 'objects-align-top',
  },
  {
    value: 'c',
    icon: 'objects-align-center-vertical',
  },
  {
    value: 'b',
    icon: 'objects-align-bottom',
  },
];

const TextAlignment = ({ textAlignment, verticalAlignment, updateData }: TextAlignmentProps) => {
  const intl = useIntl();

  const [localHorizontalAlign, setLocalHorizontalAlign] = useState(
    textAlignment ?? DEFAULT_PROPERTIES.textAlignment,
  );
  const [localVerticalAlign, setLocalVerticalAlign] = useState(
    verticalAlignment ?? DEFAULT_PROPERTIES.verticalAlignment,
  );

  const handleHorizontalChange = (newAlign: typeof textAlignment) => {
    setLocalHorizontalAlign(newAlign);
    updateData({ property: 'textAlignment', value: newAlign });
  };
  const handleVerticalChange = (newAlign: typeof verticalAlignment) => {
    setLocalVerticalAlign(newAlign);
    updateData({ property: 'verticalAlignment', value: newAlign });
  };

  return (
    <Accordion
      variant="standard"
      size="m"
      title={intl.formatMessage({ id: 'TEXT_ALIGNMENT' })}
      defaultOpen={true}
      testId="tableProperties-text-alignment"
    >
      <div className={styles.root}>
        <div className={styles.column}>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'HORIZONTAL_ALIGNMENT' })}
            testId="tableProperties-horizontal-alignment"
          >
            <div className={styles.row}>
              {HORIZONTAL_TOGGLES.map((toggle) => (
                <Toggle
                  key={`textAlignment-${toggle.value}`}
                  size="m"
                  variant="link"
                  isToggled={localHorizontalAlign === toggle.value}
                  iconProps={{ icon: toggle.icon }}
                  onToggle={() => handleHorizontalChange(toggle.value)}
                  testId={`tableProperties-horizontal-alignment-${toggle.value}`}
                />
              ))}
            </div>
          </InputField>
        </div>
        <div className={styles.column}>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'VERTICAL_ALIGNMENT' })}
            testId="tableProperties-vertical-alignment"
          >
            <div className={styles.row}>
              {VERTICAL_TOGGLES.map((toggle) => (
                <Toggle
                  key={`verticalAlignment-${toggle.value}`}
                  size="m"
                  variant="link"
                  isToggled={localVerticalAlign === toggle.value}
                  iconProps={{ icon: toggle.icon }}
                  onToggle={() => handleVerticalChange(toggle.value)}
                  testId={`tableProperties-vertical-alignment-${toggle.value}`}
                />
              ))}
            </div>
          </InputField>
        </div>
      </div>
    </Accordion>
  );
};

export default TextAlignment;
